import { FunctionComponent } from 'react'
import { SiteRFPPhaseAPIResponse } from '@black-bear-energy/black-bear-energy-common'
import { Box, Divider } from '@black-bear-energy/rucksack'
import { SearchBar } from '../common/SearchBar'
import { MenuAlign } from '../common/MenuButton'
import { FilterButton } from './FilterButton'
import { SortButton, SortButtonProps } from './SortButton'
import { ResetButton } from '../common/ResetButton'
import { FilterChip } from '../common/FilterChip'
import { getSiteRFPPhaseDisplayName } from '../helpers/DocumentExchange/getSiteRFPPhaseDisplayName'

export const SearchAndFilterBar: FunctionComponent<SearchAndFilterBarProps> = ({
  searchBarLabel,
  search,
  setSearch,
  requiredMissing,
  setRequiredMissing,
  reviewMissing,
  setReviewMissing,
  phase,
  setPhase,
  sort,
  filterAlign,
}) => {
  const allSitePhaseChips = [
    {
      key: SiteRFPPhaseAPIResponse.Contracts,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.Contracts),
    },
    {
      key: SiteRFPPhaseAPIResponse.Derisking,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.Derisking),
    },
    {
      key: SiteRFPPhaseAPIResponse.Permits,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.Permits),
    },
    {
      key: SiteRFPPhaseAPIResponse.NTP,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.NTP),
    },
    {
      key: SiteRFPPhaseAPIResponse.PreCon,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.PreCon),
    },
    {
      key: SiteRFPPhaseAPIResponse.Construction,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.Construction),
    },
    {
      key: SiteRFPPhaseAPIResponse.Complete,
      label: getSiteRFPPhaseDisplayName(SiteRFPPhaseAPIResponse.Complete),
    },
  ]
  const sitePhaseChips = allSitePhaseChips.filter((filterValue) =>
    phase.includes(filterValue.key)
  )
  const areDefaultFilterOptionsSelected =
    !requiredMissing && !reviewMissing && phase.length === 0

  const handleDelete = (chipToDelete: FilterValue) => () => {
    setPhase(phase.filter((deletedPhase) => deletedPhase !== chipToDelete.key))
  }
  const resetFilters = () => {
    setRequiredMissing(false)
    setReviewMissing(false)
    setPhase([])
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <SearchBar
          label={searchBarLabel}
          search={search}
          setSearch={setSearch}
        />
        <FilterButton
          requiredMissing={requiredMissing}
          setRequiredMissing={setRequiredMissing}
          reviewMissing={reviewMissing}
          setReviewMissing={setReviewMissing}
          phase={phase}
          setPhase={setPhase}
          handleReset={resetFilters}
          resetDisabled={areDefaultFilterOptionsSelected}
          align={filterAlign}
        />
        {sort && (
          <SortButton
            sortBy={sort.sortBy}
            setSortBy={sort.setSortBy}
            sortAsc={sort.sortAsc}
            setSortAsc={sort.setSortAsc}
            handleReset={sort.handleReset}
            resetDisabled={sort.resetDisabled}
          />
        )}
      </Box>
      {!areDefaultFilterOptionsSelected && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1 }}>
          {requiredMissing && (
            <FilterChip
              label="Documents Needed"
              onDelete={() => setRequiredMissing(false)}
            />
          )}
          {reviewMissing && (
            <FilterChip
              label="Pending Review"
              onDelete={() => setReviewMissing(false)}
            />
          )}
          {(requiredMissing || reviewMissing) && sitePhaseChips.length > 0 && (
            <Divider orientation="vertical" flexItem />
          )}
          {sitePhaseChips.map((filterValue) => (
            <FilterChip
              key={filterValue.key}
              label={filterValue.label}
              onDelete={handleDelete(filterValue)}
            />
          ))}
          <ResetButton onClick={resetFilters} />
        </Box>
      )}
    </Box>
  )
}

export interface SearchAndFilterBarProps {
  searchBarLabel: string
  search: string
  setSearch: (search: string) => void
  requiredMissing: boolean
  setRequiredMissing: (requiredMissing: boolean) => void
  reviewMissing: boolean
  setReviewMissing: (reviewMissing: boolean) => void
  phase: SiteRFPPhaseAPIResponse[]
  setPhase: (phase: SiteRFPPhaseAPIResponse[]) => void
  sort?: SortButtonProps
  filterAlign?: MenuAlign
}

interface FilterValue {
  key: SiteRFPPhaseAPIResponse
  label: string
}
